import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

type AccordionRowProps = {
  id: string;
  leadingCell?: React.ReactNode;
  summaryCells: React.ReactNode[];
  detailContent?: React.ReactNode;
  defaultExpanded?: boolean;
  expandable?: boolean;
  onRowClick?: () => void;
  summaryRowClassName?: string;
  summaryCellClassName?: string;
  detailRowClassName?: string;
  detailCellClassName?: string;
};

export default function AccordionRow({
  id,
  leadingCell,
  summaryCells,
  detailContent,
  defaultExpanded = false,
  expandable = true,
  onRowClick,
  summaryRowClassName,
  summaryCellClassName,
  detailRowClassName,
  detailCellClassName,
}: AccordionRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleRowActivate = (event: React.SyntheticEvent) => {
    if (!onRowClick) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const interactiveTarget = target?.closest('button, a, input, select, textarea, [role="button"]');
    if (interactiveTarget && interactiveTarget !== event.currentTarget) {
      return;
    }

    onRowClick();
  };

  return (
    <tbody className="group">
      <tr
        className={`${summaryRowClassName ?? 'admin-table-row'} ${onRowClick ? 'cursor-pointer' : ''}`.trim()}
        onClick={handleRowActivate}
        onKeyDown={(event) => {
          if (!onRowClick) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onRowClick();
          }
        }}
        tabIndex={onRowClick ? 0 : undefined}
        role={onRowClick ? 'button' : undefined}
        aria-label={onRowClick ? `Open details for ${id}` : undefined}
      >
        <td className="px-5 py-4 text-left align-top">
          <div className="flex items-center gap-2">
            {leadingCell}
            {expandable ? (
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={`${id}-details`}
                title={expanded ? 'Collapse details' : 'Expand details'}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-outline/60 bg-surface-container-low text-on-surface transition hover:border-primary hover:text-primary"
              >
                {expanded ? (
                  <ChevronDownIcon className="h-5 w-5" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5" />
                )}
              </button>
            ) : (
              <div className="h-9 w-9" />
            )}
          </div>
        </td>
        {summaryCells.map((cell, index) => (
          <td key={index} className={summaryCellClassName ?? 'px-6 py-4 text-sm text-on-surface'}>
            {cell}
          </td>
        ))}
      </tr>

      {expandable && detailContent ? (
        <tr
          id={`${id}-details`}
          className={expanded ? detailRowClassName ?? '' : 'hidden'}
        >
          <td colSpan={summaryCells.length + 1} className={detailCellClassName ?? 'px-6 py-4'}>
            <div className="rounded-card border border-outline/70 bg-surface-container-lowest p-4">
              {detailContent}
            </div>
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}
